import { useState } from 'react'

import { Document, Page } from 'react-pdf/dist/esm/entry.webpack'
import { Icons, Radio, Text, FullPage } from 'components'
import { CheckCircle } from 'components/elements/Icons'
import { sendDataNative } from 'components/hooks/useFetchNative'
import Title from 'components/layout/Terms/title'

import * as S from './style'
import { InsuranceCrossSellingCardProps } from './types'

const InsuranceCrossSellingCardNew = ({
  content,
  callback,
  defaultSelected = false,
  groupName = 'insurance-cross-selling',
}: InsuranceCrossSellingCardProps) => {
  const [accepted, setAccepted] = useState<boolean>(defaultSelected)
  const [openTermsPdf, setOpenTermsPdf] = useState<boolean>(false)
  const [termsPdfUrl, setTermsPdfUrl] = useState<string>('')
  const [numPages, setNumPages] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState<number>(1)

  const handleSelect = (value: boolean) => {
    setAccepted(value)
    callback(value)
  }

  const onDocumentLoadSuccess = ({ numPages: nextNumPages }: { numPages: number }) => {
    setNumPages(nextNumPages)
  }

  const handleOpenTermsPdf = (pdfUrl: string) => {
    setTermsPdfUrl(pdfUrl)
    setPageNumber(1)
    setOpenTermsPdf(true)
  }

  const handleCloseTermsPdf = () => {
    setOpenTermsPdf(false)
    setPageNumber(1)
  }

  const handleDownloadTermsPdf = () => {
    if (!termsPdfUrl) {
      return
    }

    sendDataNative<{ linkS3: string }>({
      body: {
        linkS3: termsPdfUrl,
      },
      callback: 'callbackOpenPdf',
      method: 'OPENPDF',
    })
  }

  const acceptId = `${groupName}-accept`
  const declineId = `${groupName}-decline`

  return (
    <S.InsuranceWrapper accepted={accepted}>
      <S.TagStyled>{content.tag.label}</S.TagStyled>

      <S.HeaderTitle>
        <Text variant="headline-h3" as="h3" colorWeight={500}>
          {content.title}
        </Text>
      </S.HeaderTitle>

      <S.BenefitsList>
        {content.benefits.map((benefit) => (
          <S.BenefitItem key={benefit.id}>
            <S.BenefitIconWrapper>
              <Icons variant={CheckCircle} color="var(--gray500)" width={24} height={24} />
            </S.BenefitIconWrapper>
            <Text variant="body-3" colorWeight={500} style={{ fontSize: '12px', margin: '0' }}>
              <span style={{ fontSize: '12px' }}>{benefit.description}</span>
            </Text>
          </S.BenefitItem>
        ))}
      </S.BenefitsList>

      <S.OptionsGroup role="radiogroup" aria-label={content.title}>
        <S.OptionCard type="button" onClick={() => handleSelect(true)} aria-pressed={accepted}>
          <S.OptionContent>
            <Text
              variant="caption-1"
              as="h3"
              bold
              colorWeight={500}
              style={{ fontSize: '12px', margin: '0', lineHeight: '100%' }}
            >
              {content.acceptOption.title}
              <Text
                variant="caption-1"
                as="span"
                bold
                style={{ color: 'var(--primary500)', fontSize: '12px', margin: '0' }}
              >
                {content.acceptOption.priceHighlight}
              </Text>
            </Text>
          </S.OptionContent>

          <S.RadioSlot>
            <Radio
              id={acceptId}
              name={groupName}
              value="accept"
              checked={accepted}
              onChange={() => handleSelect(true)}
              reversed
            />
          </S.RadioSlot>
        </S.OptionCard>

        <S.OptionCard type="button" onClick={() => handleSelect(false)} aria-pressed={!accepted}>
          <S.OptionContent>
            <Text
              variant="caption-1"
              as="h3"
              bold
              colorWeight={500}
              style={{ fontSize: '12px', margin: '0', lineHeight: '100%' }}
            >
              {content.declineOption.title}
            </Text>
          </S.OptionContent>

          <S.RadioSlot>
            <Radio
              id={declineId}
              name={groupName}
              value="decline"
              checked={!accepted}
              onChange={() => handleSelect(false)}
              reversed
            />
          </S.RadioSlot>
        </S.OptionCard>
      </S.OptionsGroup>

      <S.Disclaimer>
        {content.disclaimer.map((part) =>
          part.href ? (
            <S.DisclaimerLink
              key={`link-${part.text}`}
              href={part.href}
              onClick={(event) => {
                event.preventDefault()
                setTermsPdfUrl('')
                handleOpenTermsPdf(part.href as string)
              }}
            >
              {part.text}
            </S.DisclaimerLink>
          ) : (
            <span key={`text-${part.text}`}>{part.text}</span>
          ),
        )}
      </S.Disclaimer>

      <FullPage activeCloseFullPage={openTermsPdf}>
        <S.TermsPageContainer>
          <Title
            title="Termos de uso"
            onPageBack={handleCloseTermsPdf}
            onDownload={handleDownloadTermsPdf}
          />

          <S.PDFContainer>
            <Document
              file={termsPdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              error="Falha ao baixar documento!"
              loading="Carregando PDF"
            >
              <Page
                key={`page_${pageNumber + 1}`}
                pageNumber={pageNumber}
                renderAnnotationLayer={false}
                className="pdfPage"
              />
            </Document>
          </S.PDFContainer>

          <S.PDFController>
            <S.PDFControllerButton
              type="button"
              onClick={() => setPageNumber(pageNumber > 1 ? pageNumber - 1 : 1)}
              disabled={pageNumber === 1}
            >
              Anterior
            </S.PDFControllerButton>
            <Text variant="caption-1" as="span" colorWeight={500}>
              Página {pageNumber}/{numPages}
            </Text>
            <S.PDFControllerButton
              type="button"
              onClick={() => setPageNumber(pageNumber < numPages ? pageNumber + 1 : numPages)}
              disabled={pageNumber === numPages}
            >
              Próxima
            </S.PDFControllerButton>
          </S.PDFController>
        </S.TermsPageContainer>
      </FullPage>
    </S.InsuranceWrapper>
  )
}

export { InsuranceCrossSellingCardNew }
